/**
 * Landing Page Component
 *
 * Public landing page for unauthenticated users. Displays marketing content
 * and provides Google OAuth sign-in functionality for accessing the TabPicker
 * pull tabs analysis tools.
 *
 * Features:
 * - Google OAuth integration with forced re-authentication support
 * - Marketing content explaining TabPicker's value proposition
 * - Feature highlights and testimonials
 * - Responsive design optimized for conversion
 * - Automatic redirect to dashboard for authenticated users
 */

import { useState } from 'react';
import Logo from '../components/Logo';
import FallingTickets from '../components/FallingTickets';

/**
 * Landing Page Component
 *
 * Public marketing page for unauthenticated users showcasing TabPicker's
 * features and providing Google OAuth sign-in functionality.
 *
 * @returns JSX element containing the landing page interface
 */
export default function LandingPage() {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [strobeTerms, setStrobeTerms] = useState(false);

  /**
   * Handle Google OAuth login with support for forced re-authentication
   */
  const handleLogin = () => {
    if (!termsAccepted) {
      setStrobeTerms(true);
      setTimeout(() => setStrobeTerms(false), 2000); // Stop strobing after 2 seconds
      return;
    }

    // Store terms acceptance in sessionStorage to pass through OAuth flow
    sessionStorage.setItem('termsAccepted', 'true');
    sessionStorage.setItem('termsAcceptedAt', new Date().toISOString());

    // Check if we should force re-authentication (after logout)
    const forceReauth = sessionStorage.getItem('forceReauth') === 'true';

    if (forceReauth) {
      // Clear the flag and force account selection
      sessionStorage.removeItem('forceReauth');
      window.location.href = '/api/auth/google?prompt=select_account&terms_accepted=true';
    } else {
      // Normal login flow with terms acceptance flag
      window.location.href = '/api/auth/google?terms_accepted=true';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-emerald-50 to-amber-50 relative">
      {/* Animated falling tickets background */}
      <FallingTickets density="medium" speed="medium" />

      {/* Hero Section with Logo and Title */}
      <div className="container mx-auto px-2 md:px-4 py-8 md:py-12 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col gap-4 mb-6">
            {/* Logo */}
            <div className="w-full max-w-4xl mx-auto">
              <Logo className="w-full h-auto" />
            </div>

            {/* Hero text below logo */}
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-tp-cherry to-tp-gold bg-clip-text text-transparent mb-4">
                Pick Smarter
              </h1>
              <p className="text-xl md:text-2xl text-tp-line leading-relaxed">
                Pull tabs aren't a pure game of chance when you can see the jar and which major prizes have been awarded.
                Picking which game to play is the whole game.{' '}
                <strong className="bg-gradient-to-r from-tp-cherry to-tp-gold bg-clip-text text-transparent">
                  Tabpicker does the math.
                </strong>
              </p>
            </div>
          </div>

          {/* Sign In Section */}
          <div className="text-center mb-16 bg-white rounded-2xl shadow-lg p-4 tablet:p-6 border-2 border-tp-brand">
            <div className="mb-4 max-w-md mx-auto">
              <label className={`flex items-start space-x-3 text-left cursor-pointer p-3 rounded-lg transition-all duration-200 ${
                !termsAccepted ? 'hover:bg-emerald-50' : ''
              } ${strobeTerms ? 'animate-pulse bg-red-200 border-2 border-red-400' : ''}`}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 tablet:mt-1 w-12 h-12 tablet:w-10 tablet:h-10 text-tp-brand border-2 border-tp-line rounded focus:ring-tp-brand focus:ring-2"
                  required
                />
                <span className="text-xl tablet:text-2xl text-tp-line leading-relaxed">
                  I agree to the{' '}
                  <a
                    href="/terms-of-service.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-tp-brand hover:text-tp-leaf underline font-medium"
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a
                    href="/privacy-policy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-tp-brand hover:text-tp-leaf underline font-medium"
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>

            <div className="relative">
              <button
                onClick={handleLogin}
                className={`font-bold py-6 px-12 rounded-xl text-3xl tablet:text-4xl transition-all duration-200 shadow-xl transform hover:scale-105 ${
                  termsAccepted
                    ? 'bg-gradient-to-r from-tp-brand to-tp-leaf text-white hover:shadow-2xl'
                    : 'bg-emerald-50 text-tp-line/60 cursor-pointer'
                }`}
              >
                Start Picking!
              </button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid tablet:grid-cols-3 gap-6 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-md border-t-4 border-tp-brand hover:shadow-xl transition-shadow">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-3xl font-bold text-tp-brand mb-3">Analyze Odds</h3>
              <p className="text-xl text-tp-line">
                Input payout structures and get instant calculations on hold percentages
                and expected returns.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md border-t-4 border-tp-gold hover:shadow-xl transition-shadow">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-3xl font-bold text-tp-gold mb-3">Compare Games</h3>
              <p className="text-xl text-tp-line">
                Side-by-side analysis of different pull tab games to identify
                the best opportunities.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md border-t-4 border-tp-leaf hover:shadow-xl transition-shadow">
              <div className="text-5xl mb-4">💡</div>
              <h3 className="text-3xl font-bold text-tp-leaf mb-3">Smart Decisions</h3>
              <p className="text-xl text-tp-line">
                Data-driven insights to help you make informed choices about
                which games to play.
              </p>
            </div>
          </div>

          {/* Why TabPicker Section */}
          <div className="bg-gradient-to-br from-white to-emerald-50 p-10 rounded-2xl shadow-xl border-2 border-tp-brand/20">
            <h2 className="text-5xl font-bold text-center mb-10 bg-gradient-to-r from-tp-brand to-tp-leaf bg-clip-text text-transparent">
              Why TabPicker?
            </h2>

            <div className="grid tablet:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="border-l-4 border-tp-brand pl-6">
                  <h3 className="text-2xl font-bold mb-2 text-tp-brand">
                    🎲 Professional Analysis
                  </h3>
                  <p className="text-xl text-tp-line">
                    Get casino-grade statistical analysis with easy-to-understand visualizations
                    and recommendations.
                  </p>
                </div>

                <div className="border-l-4 border-tp-gold pl-6">
                  <h3 className="text-2xl font-bold mb-2 text-tp-gold">
                    💰 Save Time & Money
                  </h3>
                  <p className="text-xl text-tp-line">
                    Quickly evaluate games without manual calculations. Focus on playing,
                    not math.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="border-l-4 border-tp-leaf pl-6">
                  <h3 className="text-2xl font-bold mb-2 text-tp-leaf">
                    📈 Track Performance
                  </h3>
                  <p className="text-xl text-tp-line">
                    Save and organize your game analyses for future reference and comparison.
                  </p>
                </div>

                <div className="border-l-4 border-tp-cherry pl-6">
                  <h3 className="text-2xl font-bold mb-2 text-tp-cherry">
                    ☁️ Always Up-to-Date
                  </h3>
                  <p className="text-xl text-tp-line">
                    Cloud-based platform ensures your data is safe and accessible anywhere.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-12 text-tp-line">
            <p className="text-lg">
              © 2024 TabPicker - Play Smarter, Win More 🎰
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}